import { isEmpty } from '@nirvana/shared/basic-types/object';
import { defaultLabelRenderer } from '../../../grid/utils/renderers';
import { translateFn } from '../../../utils/translator.helper';
import {
  Events,
  IconLocation,
  LastSeenItemPart,
  PendingActivityData,
  PolicyNetworkItem,
  SecurityIssuesData,
  TagNetworkGrid,
} from './types';
import {
  ADSyncerSpecifics,
  EndpointUsers,
  ProtectedEntity,
} from '@nirvana/shared/dtos/types/protected-entities/common';
import { NetworkIconHelper } from './icons/network.icon.helper';
import { IconProperties } from '../../../ui/gzIcon/types';
import { Feature } from '@nirvana/shared/constants';
import { html } from 'lit';
import { NavigationConstants, NavigationHelper } from '@nirvana/shared/navigation';
import { htmlDecode } from '../../../utils/escape.utils';
import { ProtectedEntitiesHelper } from './protectedEntities/protected.entities.helper';

export function endpointTypeRenderer(rowData, columnDetails) {
  const endpointType: boolean | undefined = rowData.item[columnDetails.key];

  if (isEmpty(endpointType)) {
    return defaultLabelRenderer('-');
  }

  const label = endpointType ? translateFn('Server') : translateFn('Workstation');
  return defaultLabelRenderer(label);
}

export function lastSeenRenderer(rowData, columnDetails) {
  const lastSeen: LastSeenItemPart = rowData.item[columnDetails.key];
  if (lastSeen.specifics == null || lastSeen.specifics.lastSync == null) {
    return defaultLabelRenderer('-');
  }
  const label: string = Application.modules.network.controller.networkBaseController.formatLastSeenField(lastSeen);
  return defaultLabelRenderer(label);
}

export function usersRenderer(rowData, columnDetails) {
  const endpointUsers: EndpointUsers = rowData.item[columnDetails.key];
  const label: string | undefined = Application.modules.network.controller.networkBaseController.formatUsersField({
    specifics: { endpointUsers },
  });
  const dbRecord = rowData.item.dbRecord as ProtectedEntity;

  if (!label) {
    return defaultLabelRenderer('-');
  }

  const clickHandler = (event: Event) => {
    event.target?.dispatchEvent(
      new CustomEvent(Events.USER_COLUMN_CLICKED, {
        detail: dbRecord,
        bubbles: true,
        composed: true,
      })
    );
  };

  return html`
    <gz-link @click="${clickHandler}" part="label-link" id="link-label" class="d-inline"
      ><gz-text fontSize="13" class="d-inline" part="link-label">${htmlDecode(label)}</gz-text></gz-link
    >
  `;
}

export function tagsRenderer(rowData, columnDetails) {
  const gzTags: Array<TagNetworkGrid> = rowData.item[columnDetails.key];

  if (!gzTags?.length) {
    return defaultLabelRenderer('-');
  }

  const label: string | undefined =
    Application.modules.network.controller.networkBaseController.formatTagsField(gzTags);

  if (!label) {
    return defaultLabelRenderer('-');
  }

  return defaultLabelRenderer(label);
}

export function typeRenderer(rowData, columnDetails) {
  const type: number = rowData.item[columnDetails.key];

  const isFolder: boolean = ProtectedEntitiesHelper.isNetworkFolder(rowData.item.dbRecord);

  if (isFolder && type !== Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER) {
    return defaultLabelRenderer(translateFn('Group'));
  }

  switch (type) {
    case Constants.Companies.TYPE_COMPANY:
      return rowData.item.dbRecord.specifics.canBeManagedByAbove
        ? defaultLabelRenderer(translateFn('Partner-managed company'))
        : defaultLabelRenderer(translateFn('Self-managed company'));
    case Constants.ProtectedEntities.EPS.TYPE_COMPUTER:
    case Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER:
      if (rowData.item.dbRecord.specifics.isGoldenImage) {
        return defaultLabelRenderer(translateFn('Golden image, Virtual machine'));
      }
      return rowData.item.dbRecord.specifics.isVirtual
        ? defaultLabelRenderer(translateFn('Virtual machine'))
        : defaultLabelRenderer(translateFn('Physical machine'));
    case Constants.Companies.TYPE_COMPANY_FOLDER:
      return defaultLabelRenderer(translateFn('Company folder'));
    case Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER:
      return defaultLabelRenderer(translateFn('Deleted'));
    case Constants.ProtectedEntities.CONTAINERS.TYPE_CONTAINER:
      return defaultLabelRenderer(translateFn('Container'));
    case Constants.Companies.TYPE_COMPANIES_COLLECTION:
      return defaultLabelRenderer(translateFn('Company collection'));
    case Constants.Companies.TYPE_COMPANY_NETWORK:
      return defaultLabelRenderer(translateFn('Network'));
    case Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE:
      return defaultLabelRenderer(translateFn('EC2 Instance'));
    case Constants.ProtectedEntities.EPS.VMWARE_COLLECTION:
      return defaultLabelRenderer(translateFn('VMware Collection'));
    case Constants.ProtectedEntities.EPS.VMWARE_CENTER:
      return defaultLabelRenderer(translateFn('VMware Center'));
    case Constants.ProtectedEntities.EPS.VMWARE_DATACENTER:
      return defaultLabelRenderer(translateFn('VMware Datacenter'));
    case Constants.ProtectedEntities.EPS.VMWARE_FOLDER:
      return defaultLabelRenderer(translateFn('VMware Folder'));
    case Constants.ProtectedEntities.EPS.VMWARE_COMPUTE_RESOURCE:
      return defaultLabelRenderer(translateFn('VMware Compute Resource'));
    case Constants.ProtectedEntities.EPS.VMWARE_CLUSTER_COMPUTE_RESOURCE:
      return defaultLabelRenderer(translateFn('VMware Cluster Compute Resource'));
    case Constants.ProtectedEntities.EPS.VMWARE_RESOURCE_POOL:
      return defaultLabelRenderer(translateFn('VMware Resource Pool'));
    case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_APP:
      return defaultLabelRenderer(translateFn('VMware Virtual App'));
    case Constants.ProtectedEntities.EPS.VMWARE_HOST_SYSTEM:
      return defaultLabelRenderer(translateFn('VMware Host System'));
    case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_MACHINE:
      return defaultLabelRenderer(translateFn('Virtual machine'));
    default:
      console.log(`Missing translation for protected entity type: ${type}`);
      return defaultLabelRenderer('-');
  }
}

export function policyRenderer(rowData, columnDetails) {
  const policyData: PolicyNetworkItem = rowData.item[columnDetails.key];
  const { policyId, policyName } =
    Application.modules.network.controller.networkBaseController.formatPolicyField(policyData);

  if (!policyId) {
    return defaultLabelRenderer(policyName);
  }

  return html`
    <gz-link
      @click="${() => NavigationHelper.viewPolicyDetailsFromModule(policyId, NavigationConstants.NETWORK_V2.NAME)}"
      part="label-link"
      id="link-label"
      class="d-inline"
      ><gz-text fontSize="13" class="d-inline" part="link-label">${htmlDecode(policyName)}</gz-text><gz-link></gz-link
    ></gz-link>
  `;
}

export function nameRenderer(rowData, columnDetails) {
  const name: string = rowData.item[columnDetails.key];
  const iconProperties: IconProperties = NetworkIconHelper.getIconForProtectedEntity(
    rowData.item.dbRecord,
    IconLocation.GRID
  );
  return defaultIconLabelRenderer(iconProperties, name, rowData.item.dbRecord, rowData);
}

export function companyRenderer(rowData, columnDetails) {
  const label = rowData.item[columnDetails.key];
  return defaultLabelRenderer(label);
}

export function defaultIconLabelRenderer(
  iconProperties: IconProperties,
  label: string,
  protectedEntity: ProtectedEntity,
  rowData?: any
) {
  const decodedLabel = htmlDecode(label);
  let labelName = html`<gz-tooltip class="pl-1 d-inline" tooltipText="${decodedLabel}" isEllipsis
    ><gz-text fontSize="13" class="d-inline">${decodedLabel}</gz-text></gz-tooltip
  >`;

  const clickHandler = (event: Event) => {
    event.target?.dispatchEvent(
      new CustomEvent(Events.LABEL_ROW_CLICKED, {
        detail: protectedEntity,
        bubbles: true,
        composed: true,
      })
    );
  };

  if (iconProperties.isLink) {
    labelName = html`<gz-tooltip tooltipText="${decodedLabel}" isEllipsis
      ><gz-link @click="${clickHandler}" part="label-link" id="link-label" class="d-inline"
        ><gz-text fontSize="13" class="pl-1 d-inline" part="link-label">${decodedLabel}</gz-text
        ><gz-link></gz-link></gz-link
    ></gz-tooltip>`;
  }

  return html`
  <div class="d-flex flex-row align-items-center w-full h-full">
    <gz-tooltip tooltipText=${iconProperties.tooltipText}>
      <gz-icon size="20" .name="${iconProperties.icon}" .color="${iconProperties.color}" part="label-icon"></gz-icon>
    </gz-tooltip>
    <gz-tooltip part="cell-label" tooltipText="${decodedLabel}" isEllipsis>
      <gz-copy-to-clipboard class="copy-content" part="copy-to-clipboard" tooltipText="${translateFn(
        'Copy to clipboard'
      )}" .showIconOnHover="${!rowData.selected}" confirmationMessage="${translateFn('Copied')}">
        ${labelName}
      </gz-copy-to-clipboard>
    </gz-tooltip>
  </span>`;
}

export function managementStatusRenderer(rowData, columnDetails) {
  const type: number = rowData.item.dbRecord.type;
  const isManaged: boolean = !!rowData.item.dbRecord.specifics.isManaged;

  switch (type) {
    case Constants.Companies.TYPE_COMPANY:
    case Constants.Companies.TYPE_COMPANY_FOLDER:
    case Constants.Companies.TYPE_COMPANY_NETWORK:
    case Constants.Companies.TYPE_COMPANIES_COLLECTION:
    case Constants.ProtectedEntities.EPS.CUSTOM_GROUPS:
    case Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER_GROUP:
    case Constants.ProtectedEntities.EPS.ACTIVE_DIRECTORY:
    case Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP:
    case Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER:
    case Constants.ProtectedEntities.EPS.TYPE_AWS_GROUP:
    case Constants.ProtectedEntities.EPS.TYPE_AWS_ACCOUNT:
    case Constants.ProtectedEntities.EPS.TYPE_AWS_REGION:
    case Constants.ProtectedEntities.EPS.TYPE_AWS_AZ:
    case Constants.ProtectedEntities.EPS.TERMINATED_INSTANCES_GROUP:
    case Constants.ProtectedEntities.CONTAINERS.TYPE_COLLECTION:
    case Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS:
    case Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER:
    case Constants.ProtectedEntities.CONTAINERS.TYPE_HOST:
    case Constants.ProtectedEntities.EPS.VMWARE_COLLECTION:
    case Constants.ProtectedEntities.EPS.VMWARE_CENTER:
    case Constants.ProtectedEntities.EPS.VMWARE_DATACENTER:
    case Constants.ProtectedEntities.EPS.VMWARE_FOLDER:
    case Constants.ProtectedEntities.EPS.VMWARE_COMPUTE_RESOURCE:
    case Constants.ProtectedEntities.EPS.VMWARE_CLUSTER_COMPUTE_RESOURCE:
    case Constants.ProtectedEntities.EPS.VMWARE_RESOURCE_POOL:
    case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_APP:
    case Constants.ProtectedEntities.EPS.VMWARE_HOST_SYSTEM:
      return defaultLabelRenderer('-');
    default:
      return isManaged ? defaultLabelRenderer(translateFn('Managed')) : defaultLabelRenderer(translateFn('Unmanaged'));
  }
}

export function securityIssuesRenderer(rowData, columnDetails) {
  const securityIssuesData: SecurityIssuesData = rowData.item[columnDetails.key];

  if (!ProtectedEntitiesHelper.isEndpoint(rowData.item.dbRecord) || !securityIssuesData.specifics.isManaged) {
    return defaultLabelRenderer('-');
  }

  let columnLabel = securityIssuesData.hasSecurityIssues ? translateFn('With issues') : translateFn('Without issues');

  if (securityIssuesData.specifics.isIsolated) {
    columnLabel += ', ' + translateFn('Isolated');
  }

  return defaultLabelRenderer(columnLabel);
}

export function pendingActivityRenderer(rowData, columnDetails) {
  const TroubleshootingInProgressStates = [
    Constants.Troubleshooting.State.CANCELABLE,
    Constants.Troubleshooting.State.STOPPABLE,
    Constants.Troubleshooting.State.RESETTABLE,
  ];

  const pendingActivityData: PendingActivityData = rowData.item[columnDetails.key];
  const dbRecord = rowData.item.dbRecord as ProtectedEntity;

  if (!ProtectedEntitiesHelper.isEndpoint(dbRecord) || !dbRecord.specifics.isManaged) {
    return defaultLabelRenderer('-');
  }

  let pendingActivityStatus = '';

  if (pendingActivityData.pendingRestart) {
    pendingActivityStatus += translateFn('Pending restart');
  }

  if (
    pendingActivityData.pendingRestartPatch &&
    Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_VISIBLE)
  ) {
    pendingActivityStatus +=
      pendingActivityStatus !== '' ? ', ' + translateFn('Patch pending restart') : translateFn('Patch pending restart');
  }

  if (
    pendingActivityData.troubleshootingState &&
    TroubleshootingInProgressStates.includes(pendingActivityData.troubleshootingState)
  ) {
    pendingActivityStatus +=
      pendingActivityStatus !== ''
        ? ', ' + translateFn('Troubleshooting in progress')
        : translateFn('Troubleshooting in progress');
  }

  return pendingActivityStatus !== '' ? defaultLabelRenderer(pendingActivityStatus) : defaultLabelRenderer('-');
}

export function roleRenderer(rowData, columnDetails) {
  const dbRecord = rowData.item.dbRecord as ProtectedEntity;

  if (
    dbRecord.type === Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER_GROUP &&
    (dbRecord.specifics as ADSyncerSpecifics)?.isAdIntegration
  ) {
    return defaultLabelRenderer(translateFn('AD Integration'));
  }

  if (!ProtectedEntitiesHelper.isEndpoint(dbRecord) || ProtectedEntitiesHelper.isContainer(dbRecord)) {
    return defaultLabelRenderer('-');
  }

  if (!dbRecord.specifics.isManaged) {
    return defaultLabelRenderer('-');
  }

  let specialRole = '';

  if (dbRecord.specifics.isContainerHost) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('Container host');
  }

  if (dbRecord.specifics.isExchangeServer) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('Exchange Protection');
  }

  if (dbRecord.specifics.isPatchManagementServer) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('Patch Caching Server');
  }

  if (dbRecord.specifics.isSuperAgent) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('Relay');
  }

  if (dbRecord.specifics.isSva === true) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('Security Server');
  }

  if (dbRecord.specifics?.isAdSyncer === true) {
    specialRole += (specialRole.length ? ', ' : '') + translateFn('AD Integrator');
  }

  return specialRole.length ? defaultLabelRenderer(specialRole) : defaultLabelRenderer(translateFn('Endpoint'));
}

export function policyStatusRenderer(rowData, columnDetails) {
  const policyData: PolicyNetworkItem = rowData.item.policy;

  const clientRulesAppliedCondition =
    policyData.specifics.clientRules?.length && policyData.specifics.clientRules[0].policy.id;
  if (policyData.clientPolicy?.policyId || clientRulesAppliedCondition) {
    return defaultLabelRenderer(translateFn('Active'));
  }

  const assignedPolicyCondition =
    policyData.activePolicy?.confirmed === undefined && policyData.assignedPolicy?.confirmed;
  if (policyData.activePolicy?.confirmed || assignedPolicyCondition) {
    return defaultLabelRenderer(translateFn('Applied'));
  }

  const secondPendingCondition =
    policyData.activePolicy?.confirmed === undefined && policyData.assignedPolicy?.confirmed === false;
  if (policyData.activePolicy?.confirmed === false || secondPendingCondition) {
    return defaultLabelRenderer(translateFn('Pending'));
  }

  return defaultLabelRenderer('-');
}

export function policyTypeRenderer(rowData, columnDetails) {
  const policyData: PolicyNetworkItem = rowData.item.policy;

  let policyType = '';

  if (policyData.specifics.directlyApplied) {
    policyType += (policyType.length ? ', ' : '') + translateFn('Direct');
  }

  if (policyData.specifics.inherited) {
    policyType = policyType += (policyType.length ? ', ' : '') + translateFn('Inherited');
  }

  if (policyData.specifics.powerUser) {
    policyType = policyType += (policyType.length ? ', ' : '') + translateFn('Edited by Power User');
  }

  if (policyType === '') {
    return defaultLabelRenderer('-');
  }

  return defaultLabelRenderer(policyType);
}

export function powerRenderer(rowData) {
  const protectedEntity = rowData.item.dbRecord as ProtectedEntity;
  if (protectedEntity.type !== Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE) {
    return defaultLabelRenderer('-');
  }

  const powerState = protectedEntity.specifics.stateCode;

  if (powerState === Constants.ProtectedEntities.AWSPowerState.POWER_STATE_RUNNING) {
    return defaultLabelRenderer(translateFn('Running'));
  }

  if (powerState === Constants.ProtectedEntities.AWSPowerState.POWER_STATE_STOPPED) {
    return defaultLabelRenderer(translateFn('Stopped'));
  }

  if (powerState === Constants.ProtectedEntities.AWSPowerState.POWER_STATE_TERMINATED) {
    return defaultLabelRenderer(translateFn('Terminated'));
  }

  return defaultLabelRenderer('-');
}

export function integrationTagsRenderer(rowData) {
  const protectedEntity = rowData.item.dbRecord as ProtectedEntity;
  if (protectedEntity.type !== Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE) {
    return defaultLabelRenderer('-');
  }

  const tags = protectedEntity.specifics.tags;

  if (!tags || tags.length === 0) {
    return defaultLabelRenderer('-');
  }

  let integrationTag = '';
  for (const tag of tags) {
    integrationTag += tag.key + ':' + tag.value + ';';
  }

  return defaultLabelRenderer(integrationTag.substring(0, integrationTag.length - 1));
}

export function migrationRenderer(rowData) {
  const protectedEntity = rowData.item.dbRecord as ProtectedEntity;

  if (!ProtectedEntitiesHelper.isEndpoint(rowData.item.dbRecord) || !protectedEntity.specifics.isManaged) {
    return defaultLabelRenderer('-');
  }

  if (
    !protectedEntity.specifics.movingInfo ||
    protectedEntity.specifics.movingInfo.state !== Constants.ProtectedEntities.movingInfoState.SUCCESS
  ) {
    return defaultLabelRenderer(translateFn('No'));
  }

  return defaultLabelRenderer(translateFn('Yes'));
}

export function inDeletedFolderRenderer(rowData) {
  const protectedEntity = rowData.item.dbRecord as ProtectedEntity;

  if (!ProtectedEntitiesHelper.isEndpoint(rowData.item.dbRecord)) {
    return defaultLabelRenderer('-');
  }

  if (!protectedEntity.specifics.restorePath) {
    return defaultLabelRenderer(translateFn('No'));
  }

  return defaultLabelRenderer(translateFn('Yes'));
}
